import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { ChevronDownIcon } from '../ui/icons';
import { pathLabel, pathMatches, placeName } from '../locations';

/**
 * Chooses an item's place in a tree: a button showing the chosen path opens
 * a list with a filter box (typing filters on the whole path, so "salon 2"
 * finds "Salon › Armoire › Étagère 2"), a "No location" choice, and — for
 * the owner — a way to add a place inside the chosen one.
 *
 * places: the flat list of the API (depth first, with path). value: a place
 * id or null. onChange(id). onAdd(parentId, name) resolves to the new place.
 */
function LocationPicker({ places, value, onChange, onAdd, invalid, disabled, inputId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const rootRef = useRef(null);
  const filterRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);

  // Closing by a choice or Escape gives the focus back to the button.
  const close = (refocus) => {
    setOpen(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  };

  const selected = places.find(place => place.id === value) || null;
  const options = useMemo(() => {
    const matching = query.trim() ? places.filter(place => pathMatches(t, place, query)) : places;
    return [null, ...matching];   // null: no location
  }, [places, query, t]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setAddError(null);
      const index = options.findIndex(option => (option ? option.id : null) === value);
      setActive(index < 0 ? 0 : index);
      setTimeout(() => filterRef.current && filterRef.current.focus(), 0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typing puts the first matching place under Enter (not "No location").
  useEffect(() => { setActive(query.trim() && options.length > 1 ? 1 : 0); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const row = listRef.current && listRef.current.querySelector(`[data-index="${active}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (option) => {
    onChange(option ? option.id : null);
    close(true);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(i => Math.min(options.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (options[active] !== undefined) choose(options[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name || !onAdd) return;
    setAdding(true);
    setAddError(null);
    try {
      const place = await onAdd(selected ? selected.id : null, name);
      setNewName('');
      onChange(place.id);
      close(true);
    } catch (message) {
      setAddError(String(message));
    } finally {
      setAdding(false);
    }
  };

  const listId = `${inputId || 'location'}-options`;
  const label = selected ? pathLabel(t, selected.path) : t('locations.none');

  return (
    <div
      ref={rootRef}
      className="location-picker"
      // Tabbing (or clicking) out of the picker closes it.
      onBlur={(event) => {
        if (open && rootRef.current && !rootRef.current.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        ref={buttonRef}
        id={inputId}
        className={`location-picker-button${invalid ? ' is-invalid' : ''}`}
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
      >
        <span className={`location-picker-button__label${selected ? '' : ' is-empty'}`}>{label}</span>
        <ChevronDownIcon className="location-picker-button__chevron" />
      </button>

      {open && (
        <div className="location-picker-panel">
          <input
            ref={filterRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={`${listId}-${active}`}
            aria-label={t('locations.filter')}
            placeholder={t('locations.filter')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="location-picker-filter"
          />
          <ul ref={listRef} id={listId} role="listbox" className="location-picker-list">
            {options.map((option, index) => {
              const isSelected = (option ? option.id : null) === value;
              const filtering = !!query.trim();
              return (
                <li
                  key={option ? option.id : 'none'}
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  className={`location-picker-option${index === active ? ' active' : ''}${isSelected ? ' selected' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(e) => { e.preventDefault(); choose(option); }}
                  title={option ? pathLabel(t, option.path) : undefined}
                  style={{ '--depth': option && !filtering ? option.depth : 0 }}
                >
                  {option === null
                    ? <em>{t('locations.none')}</em>
                    : filtering ? pathLabel(t, option.path)
                      : <span className={option.is_unsorted ? 'location-picker-option__unsorted' : undefined}>{placeName(t, option)}</span>}
                </li>
              );
            })}
            {options.length === 1 && query.trim() && (
              <li className="location-picker-empty" aria-live="polite">{t('locations.noMatch')}</li>
            )}
          </ul>
          {onAdd && (
            <div className="location-picker-add">
              <label htmlFor={`${listId}-new`} className="location-picker-add__label">
                {selected ? t('locations.addInside', { path: pathLabel(t, selected.path) }) : t('locations.addTopLevel')}
              </label>
              <div className="location-picker-add__row">
                <input
                  id={`${listId}-new`}
                  type="text"
                  value={newName}
                  maxLength={255}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                  placeholder={t('locations.newPlace')}
                  className="location-picker-filter"
                />
                <Button size="sm" onClick={add} disabled={!newName.trim()} loading={adding}>{t('locations.add')}</Button>
              </div>
              {addError && <p role="alert" className="ui-field__error">{addError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LocationPicker;
